import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

export const RETENTION_TOTAL_STEPS = 4;

export type tRetentionProgressProps = {
  /** 1-based step this screen represents. */
  current: number;
  /** Alignment differs by step: the opening is a two-column split so its bar sits in
   *  the left column, while the single-column steps centre it. */
  className?: string;
  /**
   * Segments stretch to fill the row instead of sitting at a fixed width. The reason
   * step spans its modal; the offer steps keep the compact bar, which has to stay
   * narrow to sit under a centred heading without dominating it.
   */
  fill?: boolean;
};

/**
 * The four-segment progress indicator shared by every step of the cancellation flow.
 * Shared rather than inlined so a step cannot quietly disagree with the others about
 * how many steps there are or which one it is.
 */
export default function RetentionProgress(props: tRetentionProgressProps) {
  const { current, className, fill = false } = props;
  const { t } = useTranslation('settings/SubscriptionManager');

  return (
    <div
      className={classNames('flex gap-2', className)}
      role="progressbar"
      aria-valuemin={1}
      aria-valuemax={RETENTION_TOTAL_STEPS}
      aria-valuenow={current}
      aria-label={t('cancelSubscription.retention.progress', {
        current,
        total: RETENTION_TOTAL_STEPS,
      })}
      data-testid="retention-progress"
    >
      {Array.from({ length: RETENTION_TOTAL_STEPS }, (_, index) => (
        <span
          key={index}
          className={classNames('rounded-full', fill ? 'h-1.5 flex-1' : 'h-1 w-10', {
            'bg-primary': index < current,
            'bg-neutral-300': index >= current,
          })}
        />
      ))}
    </div>
  );
}
